import { getFullDate } from '@/helpers/date.helpers'
import { MessageModel, TemplateType } from '@/models/message.model'
import { sendMessageToZApiMultiService } from '@/redux/message/sagas/createMessageSaga'
import {
  SendMessageOfficialAPIResponse,
  SendMessageResponse
} from '@/redux/message/sagas/createMessageSaga/http/message/types'
import { differenceInMilliseconds } from 'date-fns'
import { put } from 'redux-saga/effects'
import { v4 as uuid } from 'uuid'
import { t } from 'i18next'
import { clearActiveChatAction, transferChatToOperatorAction } from '../../actions'
import { refreshChatsCountAction } from '../../handles'
import { Chat } from '../../types'
import { CreateNewMessageDTO, HandleCustomMessageDTO } from './types'
import { ChatModel } from '@/models/chat.model'
import { generateUniqueTicketNumber } from '@/redux/message/utils'
import { getTotalChatDuration } from '@/components/ChatMain/components/Header/components/Timer/helpers'
import { create, update } from '../_http/chat'
import { save } from '../_http/message'
import { sendMessageOfficialAPI } from '@/redux/message/sagas/createMessageSaga/http/message'
import { buildOfficialMessage } from '../createNewChatSaga/helpers'
import { updateChatDurationsOnClose } from '../closeChatSaga'

export function* transferChatToOperatorSaga(action: ReturnType<typeof transferChatToOperatorAction.request>) {
  const {
    currentChat,
    currentOperator,
    newOperator,
    newDepartment,
    currentCampaign,
    chatConfig,
    shareHistory,
    note,
    callBack
  } = action.payload

  if (!shareHistory) {
    yield* transferToOperatorWithoutHistory({
      currentChat,
      currentOperator,
      newOperator,
      newDepartment,
      note,
      callBack
    })
    return
  }

  try {
    let sendedMessage = null
    if (!chatConfig?.isNotSendTransferToOperatorMessage) {
      sendedMessage = yield handleCustomMessage({
        campaign: currentCampaign,
        currentChat,
        newOperator
      })
    }
    updateChatDurationsOnTransfer(currentChat, currentOperator.id)
    const dateNow = new Date().getTime()
    const updatedChatData: ChatModel = {
      ...currentChat,
      ...(sendedMessage && { lastMessage: sendedMessage }),
      department: newDepartment,
      departmentId: newDepartment.id,
      status: 'in_progress',
      operators: [
        { id: newOperator.id, name: newOperator.name, department: { id: newDepartment.id, name: newDepartment.name } }
      ],
      operatorsId: [newOperator.id],
      endDate: dateNow,
      duration: differenceInMilliseconds(new Date(dateNow), new Date(currentChat.startDate)),
      operatorsHistories: currentChat?.operatorsHistories?.length
        ? [
            ...currentChat?.operatorsHistories,
            { id: currentOperator.id, name: currentOperator.name, attend_at: dateNow }
          ]
        : [{ id: currentOperator.id, name: currentOperator.name, attend_at: dateNow }],
      updatedAt: dateNow,
      isTransfered: true,
      fixedBy: null
    }

    yield update(currentChat.id, { ...updatedChatData, updatedAt: new Date().getTime() })
    yield put(transferChatToOperatorAction.success({ transferedChat: updatedChatData }))
    yield put(refreshChatsCountAction())
    if (callBack) callBack()
    yield put(clearActiveChatAction())
  } catch (error) {
    yield put(transferChatToOperatorAction.failure())
  }
}

type TransferToOperatorWithoutHistoryDTO = {
  currentChat: Chat
  currentOperator: { id: string; name: string }
  newOperator: { id: string; name: string }
  newDepartment: Chat['department']
  note?: string
  callBack?: () => void
}

function* transferToOperatorWithoutHistory({
  currentChat,
  currentOperator,
  newOperator,
  newDepartment,
  note,
  callBack
}: TransferToOperatorWithoutHistoryDTO) {
  try {
    updateChatDurationsOnClose(currentChat, currentOperator.id)
    const dateNow = new Date().getTime()

    const closedChatData: Partial<ChatModel> = {
      id: currentChat.id,
      status: 'close',
      endDate: dateNow,
      duration: getTotalChatDuration(currentChat.durations),
      durations: currentChat.durations,
      notShareHistory: true,
      finishedBy: { id: currentOperator.id, name: currentOperator.name },
      fixedBy: null,
      updatedAt: dateNow
    }

    const baseDate = new Date(dateNow)
    const newChat: Partial<ChatModel> = {
      id: uuid(),
      ownerId: currentChat.ownerId,
      campaignId: currentChat.campaignId,
      instanceId: currentChat.instanceId,
      contactId: currentChat.contactId,
      isGroup: currentChat.isGroup,
      isContact: currentChat.isContact,
      receiverNumber: currentChat.receiverNumber,
      receiverId: currentChat.receiverId,
      requesterNumber: currentChat.requesterNumber,
      requesterPhoto: currentChat.requesterPhoto,
      chatName: currentChat.chatName,
      chatNameLowercase: currentChat.chatNameLowercase,
      department: newDepartment,
      departmentId: newDepartment.id,
      status: 'in_progress',
      unreadMessagesCount: 0,
      operators: [
        { id: newOperator.id, name: newOperator.name, department: { id: newDepartment.id, name: newDepartment.name } }
      ],
      operatorsId: [newOperator.id],
      startDate: dateNow,
      endDate: null,
      month: baseDate.getMonth(),
      year: baseDate.getFullYear(),
      fullDate: getFullDate(baseDate),
      isFinishedUra: false,
      duration: 0,
      durations: [],
      ticket: generateUniqueTicketNumber(),
      lastMessage: null,
      isTransfered: true,
      notShareHistory: false,
      ...(note?.trim() && {
        notes: [
          {
            id: uuid(),
            title: t('translation:transferChatModalAutoNoteTitle'),
            content: note,
            operator: { id: currentOperator.id, name: currentOperator.name },
            createdAt: dateNow,
            updatedAt: dateNow
          }
        ]
      }),
      createdAt: dateNow,
      updatedAt: dateNow
    }

    yield update(currentChat.id, closedChatData)
    yield create(newChat as ChatModel)
    yield put(transferChatToOperatorAction.success({ transferedChat: { ...currentChat, ...closedChatData } }))
    yield put(refreshChatsCountAction())
    if (callBack) callBack()
    yield put(clearActiveChatAction())
  } catch (error) {
    console.log('transferChatToOperatorWithoutHistory error', error)
    yield put(transferChatToOperatorAction.failure())
  }
}

async function handleCustomMessage({ campaign, currentChat, newOperator }: HandleCustomMessageDTO) {
  const newMessage = await createNewMessage({
    campaign,
    currentChat,
    newOperator: { id: newOperator.id, name: newOperator.name },
    senderId: newOperator.id
  })

  if (campaign?.isOfficial) {
    const sendMessageOfficialAPIResponse: SendMessageOfficialAPIResponse = await sendMessageOfficialAPI({
      campaignId: campaign.id,
      messageId: newMessage?.templateMessageId,
      phone: newMessage.phone,
      typeSent: newMessage.messageTypeSent,
      phoneNumberId: campaign.phoneNumberId,
      text: newMessage.messageText,
      messageType: newMessage.typeFile
    })
    const sendedMessages = buildOfficialMessage(newMessage, sendMessageOfficialAPIResponse)
    const lastMessage = sendedMessages[sendedMessages.length - 1]
    await save(lastMessage)
    return lastMessage
  } else {
    const sendMessageResult: SendMessageResponse = await sendMessageToZApiMultiService(
      newMessage,
      campaign.id,
      campaign.token
    )

    newMessage.messageId = sendMessageResult.messageId
    await save(newMessage)
    return newMessage
  }
}

async function createNewMessage({
  campaign,
  currentChat,
  newOperator,
  senderId
}: CreateNewMessageDTO): Promise<MessageModel> {
  return {
    campanhaId: campaign.id,
    ownerId: campaign.ownerId,
    files: [],
    typeFile: 'text',
    messageText: `Atendimento sendo transferido para o operador *${newOperator.name}*, aguarde um momento.`,
    messageTypeSent: TemplateType.SOMENTE_TEXTO_SEM_TEMPLATE_SEM_PARAMETRO,
    senderNumber: currentChat.requesterNumber,
    phone: currentChat.requesterNumber,
    senderId,
    connectedPhone: currentChat.receiverNumber,
    receiverNumber: currentChat.receiverNumber,
    chatId: currentChat.id,
    messageId: '',
    isRead: false,
    isSend: true,
    fromMe: true,
    createdAt: new Date().getTime()
  } as MessageModel
}

function updateChatDurationsOnTransfer(activeChat: ChatModel, operatorId: string) {
  const operatorDuration = findOperatorDuration(activeChat, operatorId)
  if (operatorDuration) {
    const currentPeriod = operatorDuration.periods.at(-1)
    if (currentPeriod && !currentPeriod?.endTime) {
      currentPeriod.endTime = Date.now()
    }
  }
}

function findOperatorDuration(activeChat: ChatModel, operatorId: string) {
  const durations = activeChat?.durations ?? []
  const operatorDuration = durations.find(duration => duration.operatorId === operatorId)
  return operatorDuration
}
